/**
 * The reducer — the single write path for the whole product.
 *
 * ```
 * applyCommand(state, command) → { ok: true, state } | { ok: false, error }
 * ```
 *
 * Guarantees, all of them load-bearing for PLAN §8.3's sync design:
 *
 * - **Pure.** No clock, no randomness, no IO. Every timestamp and every id
 *   arrives on the command.
 * - **Immutable, with structural sharing.** `state` is never mutated; the
 *   result shares every untouched subtree.
 * - **Total.** An unknown command type or an invalid payload returns a typed
 *   error. Nothing in this module throws, so a corrupt entry in a replayed log
 *   cannot take down a Durable Object mid-catch-up.
 * - **Deterministic.** The same initial state and the same command sequence
 *   produce a deep-equal workspace, on any device, in any runtime.
 */

import type { z } from 'zod';
import type { WorkspaceState } from '../document/workspace';
import type { Actor, ApplyResult, CommandError } from './envelope';
import { commandError, issuesFromZod } from './envelope';
import { COMMAND_DEFINITIONS, isCommandType } from './registry';
import type { Command, CommandOf, CommandType } from './types';

/**
 * A definition with its generic erased.
 *
 * The registry lookup is the one place the command name is a value rather than
 * a literal type, so the per-command payload/apply pairing cannot be expressed
 * there. Soundness is restored immediately after by re-validating the payload
 * against that same definition's schema before `apply` sees it.
 */
interface ErasedDefinition {
  readonly payload: z.ZodType<unknown>;
  readonly apply: (state: WorkspaceState, command: Command) => ApplyResult;
  readonly invert: (state: WorkspaceState, command: Command) => Command | null;
  readonly summarize: (state: WorkspaceState, command: Command) => string;
}

/**
 * Resolve a command to its definition and a **validated** payload.
 *
 * The payload is re-validated on every apply rather than trusted from the type
 * system, because commands cross a wire: they arrive from IndexedDB, from a
 * WebSocket, and from the AI assistant, none of which TypeScript can vouch for.
 */
function resolve(
  command: Command,
):
  | { ok: true; definition: ErasedDefinition; command: Command }
  | { ok: false; error: CommandError } {
  if (!isCommandType(command.type)) {
    return {
      ok: false,
      error: commandError('unknown-command', `Unknown command type "${command.type}".`, {
        commandId: command.commandId,
        commandType: command.type,
      }),
    };
  }
  const definition = COMMAND_DEFINITIONS[command.type] as unknown as ErasedDefinition;
  const parsed = definition.payload.safeParse(command.payload);
  if (!parsed.success) {
    return {
      ok: false,
      error: commandError('invalid-payload', `Invalid payload for "${command.type}".`, {
        commandId: command.commandId,
        commandType: command.type,
        issues: issuesFromZod(parsed.error),
      }),
    };
  }
  return { ok: true, definition, command: { ...command, payload: parsed.data } as Command };
}

/**
 * Apply one command.
 *
 * @param state - The workspace before the command. Not mutated.
 * @param command - The command to apply. Its payload is re-validated here.
 * @returns The next workspace, or a typed error. Never throws.
 */
export function applyCommand(state: WorkspaceState, command: Command): ApplyResult {
  const resolved = resolve(command);
  if (!resolved.ok) return { ok: false, error: resolved.error };
  return resolved.definition.apply(state, resolved.command);
}

/**
 * Apply a sequence of commands, stopping at the first failure.
 *
 * The whole batch is discarded on failure — the caller gets the original state
 * back alongside the error, with `error.index` naming the offending position.
 * All-or-nothing is the right semantics for both consumers: a sync catch-up
 * that half-applies is a corrupt replica, and a partially-accepted AI changeset
 * is a recipe nobody asked for.
 *
 * @param state - The workspace before the batch. Not mutated.
 * @param commands - The commands, in order.
 */
export function applyLog(state: WorkspaceState, commands: readonly Command[]): ApplyResult {
  let current = state;
  for (const [index, command] of commands.entries()) {
    const result = applyCommand(current, command);
    if (!result.ok) return { ok: false, error: { ...result.error, index } };
    current = result.state;
  }
  return { ok: true, state: current };
}

/**
 * The command that undoes `command`, computed against the **pre**-state.
 *
 * Must be called *before* the command is applied: `add` needs to know nothing,
 * but `update` needs the prior field values, `remove` needs the item and its
 * position, and `delete` needs the entire document. The returned envelope
 * carries the pre-state document's `updatedAt` as its timestamp, so applying it
 * restores the modification time along with the content and
 * `apply(invert(s, c), apply(s, c)) === s` holds exactly.
 *
 * @returns The inverse command, or `null` when the command does not apply to
 *   this state (nothing to undo) or is not invertible.
 */
export function invert(state: WorkspaceState, command: Command): Command | null {
  const resolved = resolve(command);
  if (!resolved.ok) return null;
  return resolved.definition.invert(state, resolved.command);
}

/**
 * One line of English describing what `command` does to `state`.
 *
 * Computed against the pre-state so it can render a transition
 * ("Crystal 60 → Crystal 40") rather than only the new value. Falls back to the
 * bare command name for anything unrecognised, and never throws.
 */
export function describeCommand(state: WorkspaceState, command: Command): string {
  const resolved = resolve(command);
  if (!resolved.ok) return command.type;
  return resolved.definition.summarize(state, resolved.command);
}

/**
 * Build a command envelope with the right payload type for its name.
 *
 * A convenience for tests, the UI's dispatcher and the assistant's tool
 * handler — it is exactly the object literal, but with `type` and `payload`
 * checked against each other.
 */
export function makeCommand<K extends CommandType>(
  type: K,
  payload: CommandOf<K>['payload'],
  envelope: {
    readonly commandId: string;
    readonly timestamp: number;
    readonly actor: Actor;
  },
): CommandOf<K> {
  return { type, payload, ...envelope };
}
